import { Stack, styled } from '@mui/material';
import {
  Provider,
  type BackupJob,
  type InventoryResource,
  type ResourceType,
  type RestoreJob,
} from '@repo/api-gw-sdk';
import type { ReactNode } from 'react';

import { CloudProviders } from '@/data/inventory/data';
import { ResourceTypes } from '@/data/inventory/resourceTypes';

import { isJob } from './queryBuilder/jobTypeGuards';
import { CircleImage } from './shared/circleImage';
import { OverflowTooltip } from './tooltips/overflowTooltip';

export const CloudProviderIndicator = styled(
  ({
    cloudProvider,
    text,
    className,
  }: {
    cloudProvider: Provider;
    text?: ReactNode;
    className?: string;
  }) => {
    return (
      <Stack
        direction='row'
        className={className}
        sx={{
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <CloudProviderCircle cloudProvider={cloudProvider} />
        {!text || typeof text === 'string' ? (
          <OverflowTooltip
            text={
              (text as string) ||
              CloudProviders[cloudProvider]?.displayName ||
              cloudProvider
            }
          />
        ) : (
          text
        )}
      </Stack>
    );
  }
)({});

export const CloudProviderCircle = ({
  cloudProvider,
}: {
  cloudProvider: Provider;
}) => {
  return (
    CloudProviders[cloudProvider] && (
      <CircleImage
        className='circle-image'
        alt={CloudProviders[cloudProvider]?.displayName}
        src={CloudProviders[cloudProvider]?.logo}
        srcDark={CloudProviders[cloudProvider]?.logoDark}
      />
    )
  );
};

export const ResourceTypeIndicator = ({
  resourceType,
}: {
  resourceType: ResourceType;
}) => {
  const cloudProvider = ResourceTypes[resourceType]?.provider;

  return (
    <CloudProviderIndicator
      cloudProvider={cloudProvider}
      text={ResourceTypes[resourceType]?.displayName}
    />
  );
};

export const getCloudProvider = (
  entity: InventoryResource | RestoreJob | BackupJob
): Provider => {
  return isJob(entity)
    ? entity.resourceDetails?.cloudProvider || Provider.ProviderUnspecified
    : entity.cloudProvider;
};
